import { useTranslation } from "react-i18next";
import { DialogProps } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ModalBasic } from "../../../../../shared/components/ModalBasic";
import { StorageScope } from "../../../../../services/api/instances/storageApi/types/service_types";
import { useDeleteObjectModal } from "../../hook/use-delete-object-modal";
import { AlertIcon } from "../../../../../shared/icons/AlertIcon";
import { TEntityInfo } from "../../../../types/utility";
import { TModalsIds } from "../../../../../app/state/modalSlice/types";
import styles from "./styles.module.css";

interface Props extends Omit<DialogProps, "open"> {
  service: StorageScope;
  closeModal: (id: TModalsIds, subtype?: string) => void;
  handleDeleteEntity?: (info: TEntityInfo | null) => void;
  subtype?: string;
}

export const DeleteEntityModal = ({
  service,
  subtype,
  handleDeleteEntity,
  closeModal,
  ...rest
}: Props) => {
  const { deleteModal, handleDelete } = useDeleteObjectModal(service);
  const { t } = useTranslation();

  const extraPart = useMemo(() => {
    return deleteModal?.info?.extra === "parent"
      ? "(если внутри папки есть объекты они будут перенесены в корень)"
      : "";
  }, [deleteModal.isOpen, deleteModal?.info?.extra]);

  const onDelete = useCallback(() => {
    closeModal("delete_modal", "delete_object");
    if (deleteModal.subtype === "delete_object") {
      handleDelete(deleteModal?.info?.id ?? "");
    } else if (deleteModal.subtype === "delete_entity" && handleDeleteEntity) {
      handleDeleteEntity(deleteModal.info);
    }
  }, [deleteModal.subtype, deleteModal.isOpen]);

  const handleCloseModal = useCallback(() => {
    if (deleteModal.subtype === "delete_object") {
      closeModal("delete_modal", "");
    } else if (deleteModal.subtype === "delete_entity") {
      closeModal("delete_modal", "");
    }
  }, [closeModal, deleteModal.subtype, deleteModal.isOpen]);

  if (!deleteModal.isOpen) return null;

  return (
    <ModalBasic
      open={deleteModal.isOpen && deleteModal.subtype === subtype}
      onOk={onDelete}
      onCancel={handleCloseModal}
      onClose={handleCloseModal}
      buttonOkText={t("delete_modal.delete_button")}
      hasHeader={false}
      isWarningButton
      hasFooter
      {...rest}
    >
      <div className={styles["warning-container"]}>
        <AlertIcon className={styles["warning-icon"]} />
        <div className={styles.warning}>
          {t("delete_modal.title")}{" "}
          <span className={styles.name}>
            {`${deleteModal.info?.value}` || ""}
          </span>
          {extraPart && ` ${extraPart}`}?
        </div>
      </div>
    </ModalBasic>
  );
};
